// AnalyticsManager: one tiny surface the game calls — analytics.event(name, props)
// — routed to the configured provider. Auto-tags the stream as
// "<gameId>-<platform>" so web and Android show up separately.
//
// Never throws into gameplay: unknown provider, missing keys, or a provider
// error all degrade to a debug/warn log.

import { ConsoleAnalyticsProvider } from './providers/consoleAnalyticsProvider.js';
import { GameAnalyticsProvider } from './providers/gameAnalyticsProvider.js';
import { isDevEnvironment } from '../platform/env.js';

export class AnalyticsManager {
  constructor(cfg, ctx = {}) {
    this.cfg = cfg || {};
    this.log = ctx.log || console;
    this.appId = ctx.appId || 'game';     // e.g. "game-name-web"
    this.platform = ctx.platform || 'web';
    this.version = ctx.version || '1.0.0';
    this.isDev = isDevEnvironment();
    this.provider = null;
  }

  _make(name) {
    const ctx = { log: this.log, appId: this.appId, platform: this.platform, version: this.version };
    if (name === 'gameanalytics') return new GameAnalyticsProvider(this.cfg, ctx);
    if (name === 'console') return new ConsoleAnalyticsProvider(ctx);
    if (name === 'none') return null;
    this.log.warn(`[analytics] unknown provider "${name}"`);
    return null;
  }

  async init() {
    if (!this.cfg.enabled) { this.log.debug('[analytics] disabled'); return; }

    // Local-dev handling.
    if (this.isDev && this.cfg.disableInDev) {
      this.log.info('[analytics] disabled in dev (disableInDev)');
      return;
    }

    // In dev, prefer devProvider (e.g. 'console') so local runs don't pollute
    // production GameAnalytics. In production, always use `provider`.
    const primaryName = (this.isDev && this.cfg.devProvider) ? this.cfg.devProvider : this.cfg.provider;
    this.provider = this._make(primaryName);

    let result = { ok: true };
    if (this.provider) {
      result = (await this.provider.init?.().catch((err) => {
        this.log.warn('[analytics] init issue', err);
        return { ok: false, reason: 'error' };
      })) || { ok: true };
    }

    // Reactive safety net: if the primary failed in dev and a devProvider exists
    // (and we weren't already using it), degrade to it — one clear warning.
    if (!result.ok && this.isDev && this.cfg.devProvider && primaryName !== this.cfg.devProvider) {
      this.log.warn(`[analytics] "${primaryName}" unavailable in dev (${result.reason || 'failed'}); using "${this.cfg.devProvider}"`);
      this.provider = this._make(this.cfg.devProvider);
      await this.provider?.init?.().catch(() => {});
    }

    this.log.debug(`[analytics] ready: stream "${this.appId}" via ${primaryName}${this.isDev ? ' (dev)' : ''}`);
  }

  // Log an event. name is required; props is an optional flat object (a numeric
  // `value` is passed through to providers that support it, e.g. GameAnalytics).
  event(name, props) {
    if (typeof name !== 'string' || !name) { this.log.warn('[analytics] event name must be a non-empty string'); return; }
    if (!this.cfg.enabled) { this.log.debug('[analytics] (disabled) event', name, props || ''); return; }
    if (!this.provider) return;
    try { this.provider.event(name, props || {}); }
    catch (err) { this.log.warn('[analytics] event error', err); }
  }
}
